// src/components/PropertyFields.js
// import React, { useState } from 'react';
import { states } from '../../utils/states';
import { formatCurrencyForDisplay } from '../../utils/currencyFormatter';
import { format } from 'date-fns';
import { PropertyTypeOptions, PropertyStatusOptions } from '../../utils/enums'; // Import new enums
import AddressSearch from '../AddressSearch-Selector';
import ImageManager from '../ImageManager';

const requiredFields = ['address', 'city', 'state', 'zipCode', 'type', 'price', 'status'];

const isRequired = (field) => requiredFields.includes(field);

const PropertyFields = ({
  activeTab,
  formData,
  handleChange,
  handleAddressSelect,
  isReadOnly,
  currentYear,
  mode,
  displayAddress,
  fieldErrors = {},
  onImageUpload,
  onImageDelete,
  onDeleteStagedImage,
  onSetMainImage,
  onImageRotate,
  onSetMainStagedImage,
  showAppNotification,
  uploadingFiles,
  stagedImages, // New prop for staged images before saving
  user,
}) => {
  if (!formData) return null; // Prevent rendering until formData is ready

  const formattedCreatedOn = formData.createdOn ? format(new Date(formData.createdOn), 'MMM-dd-yyyy HH:mm') : 'N/A';

  const formatRoiRange = (roi) => {
    if (!roi || (roi.min === null && roi.max === null && roi.min === undefined && roi.max === undefined)) return 'N/A';
    const min = roi.min !== null && roi.min !== undefined ? `${roi.min.toFixed(1)}%` : null;
    const max = roi.max !== null && roi.max !== undefined ? `${roi.max.toFixed(1)}%` : null;
    if (min && max) {
      if (min === max) return min;
      return `${min} - ${max}`;
    }
    return min || max || 'N/A';
  };
  const displayRoi = formatRoiRange(formData.roi);

  const handleNumericBlur = (e) => {
    const { name, value } = e.target;
    if (value) {
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue)) {
        const formattedValue = numericValue.toFixed(2);
        // Create a synthetic event to pass to the parent's handleChange
        handleChange({ target: { name, value: formattedValue } });
      }
    }
  };

  const renderOverviewLocationFields = () => (
    <>
      {/* {(mode === 'edit' || mode === 'view') && formData.propertyID && (
        <div className="form-group">
          <label htmlFor="propertyID">Property ID:</label>
          <input
            type="number"
            id="propertyID"
            name="propertyID"
            value={formData.propertyID}
            readOnly // Always read-only as it's system-generated or an identifier
            disabled // Visually indicate it's not editable
            className={fieldErrors.propertyID ? 'input-error' : ''}
          />
          {fieldErrors.propertyID && <div className="field-error">{fieldErrors.propertyID}</div>}
        </div>
      )} */}
      <div className="form-row-address">
        <div className="form-group address-search-group">
          <label htmlFor="address">Address Search{isRequired('address') && <span className="required-asterisk">*</span>}:</label>
          <AddressSearch id="address" initialValue={displayAddress} onLocationSelect={handleAddressSelect} disabled={isReadOnly} required={isRequired('address')} placeholder="Start typing address..." />
        </div>

        <div className="form-group city-group">
          <label htmlFor="city">City{isRequired('city') && <span className="required-asterisk">*</span>}:</label>
          <input type="text" id="city" name="city" value={formData.city} onChange={handleChange} required={isRequired('city')} disabled={isReadOnly} />
        </div>
        <div className="form-group state-group">
          <label htmlFor="state">State{isRequired('state') && <span className="required-asterisk">*</span>}:</label>
          <select id="state" name="state" value={formData.state} onChange={handleChange} required={isRequired('state')} disabled={isReadOnly}>
            <option value="">Select State</option>
            {states.map((state) => (
              <option key={state.value} value={state.value}>
                {state.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group zip-group">
          <label htmlFor="zipCode">Zip Code{isRequired('zipCode') && <span className="required-asterisk">*</span>}:</label>
          <input type="text" id="zipCode" name="zipCode" value={formData.zipCode} onChange={handleChange} required={isRequired('zipCode')} disabled={isReadOnly} maxLength="10" />
        </div>
      </div>
      <div className="form-row">
        {' '}
        {/* New wrapper div */}
        <div className="form-group">
          <label htmlFor="type">Type{isRequired('type') && <span className="required-asterisk">*</span>}:</label>
          <select id="type" name="type" value={formData.type} onChange={handleChange} required={isRequired('type')} disabled={isReadOnly}>
            <option value="">Select Type</option>
            {PropertyTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="status">Status{isRequired('status') && <span className="required-asterisk">*</span>}:</label>
          <select id="status" name="status" value={formData.status} onChange={handleChange} required={isRequired('status')} disabled={isReadOnly}>
            {/* <option value="">Select Status</option> */} {/* Default is usually set */}
            {PropertyStatusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        {user?.isAdmin && (
          <div className="form-group">
            <label htmlFor="featured">Featured Priority:</label>
            <input type="number" id="featured" name="featured" value={formData.featured ?? 0} onChange={handleChange} disabled={isReadOnly} min="0" />
          </div>
        )}
        <div className="form-group">
          <label htmlFor="featuredUntil">Featured Until:</label>
          <input type="date" id="featuredUntil" name="featuredUntil" value={formData.featuredUntil ? new Date(formData.featuredUntil).toISOString().split('T')[0] : ''} onChange={handleChange} disabled={!user?.isAdmin || isReadOnly} />
        </div>
      </div>
      <div className="form-group">
        <label htmlFor="description">Description:</label>
        <textarea id="description" name="description" value={formData.description} onChange={handleChange} readOnly={isReadOnly} />
      </div>
      <div className="form-group">
        <label htmlFor="notesToAI">Notes for AI:</label>
        <textarea
          id="notesToAI"
          name="notesToAI"
          value={formData.notesToAI}
          onChange={handleChange}
          readOnly={isReadOnly}
          placeholder="Highly recommended: Help the AI highlight this property's best features. Mention any recent developments, unique local attractions, or future potential an automated system might miss."
        />
      </div>
      {(mode === 'edit' || mode === 'view') && (
        <div className="form-group">
          <label htmlFor="createdOn">Created On:</label>
          <input type="text" id="createdOn" name="createdOn" value={formattedCreatedOn} readOnly disabled />
        </div>
      )}
    </>
  );

  const renderFinancialsROIFields = () => (
    <>
      {/* <h3>Initial Costs & Income</h3>
      {fieldErrors.price && <div className="field-error">{fieldErrors.price}</div>}

      <h3>Financials</h3> */}
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="price">Price{isRequired('price') && <span className="required-asterisk">*</span>}:</label>
          <input type="text" id="price" name="price" value={formatCurrencyForDisplay(formData.price)} onChange={handleChange} required={isRequired('price')} readOnly={isReadOnly} />
        </div>

        <div className="form-group">
          {fieldErrors.inspectionCost && <div className="field-error">{fieldErrors.inspectionCost}</div>}
          <label htmlFor="inspectionCost">Inspection Cost:</label>
          <input type="text" id="inspectionCost" name="inspectionCost" value={formatCurrencyForDisplay(formData.inspectionCost)} onChange={handleChange} readOnly={isReadOnly} />
        </div>

        <div className="form-group">
          {fieldErrors.closingCosts && <div className="field-error">{fieldErrors.closingCosts}</div>}
          <label htmlFor="closingCosts">Closing Costs:</label>
          <input type="text" id="closingCosts" name="closingCosts" value={formatCurrencyForDisplay(formData.closingCosts)} onChange={handleChange} readOnly={isReadOnly} />
        </div>
      </div>

      <div className="form-row form-row-split-dynamic">
        {' '}
        {/* Added new class for specific layout */}
        <div className="form-group">
          {fieldErrors.additionalCosts && <div className="field-error">{fieldErrors.additionalCosts}</div>}
          <label htmlFor="additionalCosts">Additional Initial Costs:</label>
          <input type="text" id="additionalCosts" name="additionalCosts" value={formatCurrencyForDisplay(formData.additionalCosts)} onChange={handleChange} readOnly={isReadOnly} />
        </div>
        <div className="form-group additional-costs-description-group">
          {' '}
          {/* Added specific class */}
          {fieldErrors.additionalCostsDescription && <div className="field-error">{fieldErrors.additionalCostsDescription}</div>}
          <label htmlFor="additionalCostsDescription">Additional Costs Description:</label>
          <textarea id="additionalCostsDescription" name="additionalCostsDescription" value={formData.additionalCostsDescription} onChange={handleChange} readOnly={isReadOnly} />
        </div>
      </div>

      {fieldErrors.rent && <div className="field-error">{fieldErrors.rent}</div>}
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="rent.min">Min Rent Estimate/mo:</label>
          <input type="text" id="rent.min" name="rent.min" value={formatCurrencyForDisplay(formData.rent?.min)} onChange={handleChange} readOnly={isReadOnly} placeholder="$0" />
        </div>
        <div className="form-group">
          <label htmlFor="rent.max">Max Rent Estimate/mo:</label>
          <input type="text" id="rent.max" name="rent.max" value={formatCurrencyForDisplay(formData.rent?.max)} onChange={handleChange} readOnly={isReadOnly} placeholder="$0" />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="roi">Calculated ROI %:</label>
        <input type="text" id="roi" name="roi" value={displayRoi} readOnly disabled className="calculated-field" />
      </div>
    </>
  );

  const renderRecurringExpensesFields = () => (
    <>
      {/* <h4>Recurring Expenses</h4> */}
      <div className="form-row">
        <div className="form-group">
          {fieldErrors.hoa && <div className="field-error">{fieldErrors.hoa}</div>}
          <label htmlFor="hoa">HOA/mo:</label>
          <input type="text" id="hoa" name="hoa" value={formatCurrencyForDisplay(formData.hoa)} onChange={handleChange} readOnly={isReadOnly} />
        </div>
        <div className="form-group">
          {fieldErrors.managementFee && <div className="field-error">{fieldErrors.managementFee}</div>}
          <label htmlFor="managementFee">Management Fee %:</label>
          <input type="number" id="managementFee" name="managementFee" value={formData.managementFee} onChange={handleChange} readOnly={isReadOnly} min="0" step="0.1" />
          {!isReadOnly && <span className="input-unit">%</span>} {/* Add percentage indicator */}
        </div>
        <div className="form-group">
          {fieldErrors.maintenance && <div className="field-error">{fieldErrors.maintenance}</div>}
          <label htmlFor="maintenance">Maintenance/yr (default 1% of price):</label> {/* Label changed */}
          <input type="text" id="maintenance" name="maintenance" value={formatCurrencyForDisplay(formData.maintenance)} onChange={handleChange} readOnly={isReadOnly} placeholder="$0" />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          {fieldErrors.utilities && <div className="field-error">{fieldErrors.utilities}</div>}
          <label htmlFor="utilities">Utilities/mo (if paid by owner):</label>
          <input type="text" id="utilities" name="utilities" value={formatCurrencyForDisplay(formData.utilities)} onChange={handleChange} readOnly={isReadOnly} />
        </div>
        <div className="form-group">
          {fieldErrors.propertyTax && <div className="field-error">{fieldErrors.propertyTax}</div>}
          <label htmlFor="propertyTax">Property Tax/yr:</label>
          <input type="text" id="propertyTax" name="propertyTax" value={formatCurrencyForDisplay(formData.propertyTax)} onChange={handleChange} readOnly={isReadOnly} />
        </div>
        <div className="form-group">
          {fieldErrors.insurance && <div className="field-error">{fieldErrors.insurance}</div>}
          <label htmlFor="insurance">Insurance/yr:</label>
          <input type="text" id="insurance" name="insurance" value={formatCurrencyForDisplay(formData.insurance)} onChange={handleChange} readOnly={isReadOnly} placeholder="$0" />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          {fieldErrors.vacancyRate && <div className="field-error">{fieldErrors.vacancyRate}</div>}
          <label htmlFor="vacancyRate">Vacancy Rate (%):</label>
          <input type="number" id="vacancyRate" name="vacancyRate" value={formData.vacancyRate} onChange={handleChange} readOnly={isReadOnly} min="0" max="100" step="0.1" />
        </div>
        <div className="form-group">
          {fieldErrors.otherExpenses && <div className="field-error">{fieldErrors.otherExpenses}</div>}
          <label htmlFor="otherExpenses">Other Expenses/mo:</label>
          <input type="text" id="otherExpenses" name="otherExpenses" value={formatCurrencyForDisplay(formData.otherExpenses)} onChange={handleChange} readOnly={isReadOnly} />
        </div>
        <div className="form-group">
          {fieldErrors.otherExpensesDescription && <div className="field-error">{fieldErrors.otherExpensesDescription}</div>}
          <label htmlFor="otherExpensesDescription">Other Expenses Description:</label>
          <textarea id="otherExpensesDescription" name="otherExpensesDescription" value={formData.otherExpensesDescription} onChange={handleChange} readOnly={isReadOnly} />
        </div>
      </div>
    </>
  );

  const renderPropertyDetailsFields = () => (
    <>
      {/* <h3>Property Details</h3> */}
      <div className="form-row">
        <div className="form-group">
          {fieldErrors.beds && <div className="field-error">{fieldErrors.beds}</div>}
          <label htmlFor="beds">Beds:</label>
          <input type="number" id="beds" name="beds" value={formData.beds} onChange={handleChange} min="0" step="1" readOnly={isReadOnly} />
        </div>
        <div className="form-group">
          {fieldErrors.baths && <div className="field-error">{fieldErrors.baths}</div>}
          <label htmlFor="baths">Baths:</label>
          <input type="number" id="baths" name="baths" value={formData.baths} onChange={handleChange} min="0" step="0.5" readOnly={isReadOnly} />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          {fieldErrors.size && <div className="field-error">{fieldErrors.size}</div>}
          <label htmlFor="size">Size (sq ft):</label>
          <input type="number" id="size" name="size" value={formData.size} onChange={handleChange} min="0" step="1" readOnly={isReadOnly} />
        </div>
        <div className="form-group">
          {fieldErrors.lotSize && <div className="field-error">{fieldErrors.lotSize}</div>}
          <label htmlFor="lotSize">Lot Size (acres):</label>
          <input type="number" id="lotSize" name="lotSize" value={formData.lotSize} onChange={handleChange} readOnly={isReadOnly} placeholder="0.00" onBlur={handleNumericBlur} step="0.01" />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          {fieldErrors.tenantInPlace && <div className="field-error">{fieldErrors.tenantInPlace}</div>}
          <label htmlFor="tenantInPlace">Tenant Occupied:</label>
          <input type="checkbox" id="tenantInPlace" name="tenantInPlace" checked={!!formData.tenantInPlace} onChange={handleChange} disabled={isReadOnly} />
        </div>
        <div className="form-group">
          {fieldErrors.yearBuilt && <div className="field-error">{fieldErrors.yearBuilt}</div>}
          <label htmlFor="yearBuilt">Year Built:</label>
          <input type="number" id="yearBuilt" name="yearBuilt" value={formData.yearBuilt} onChange={handleChange} min="1800" max={currentYear} step="1" readOnly={isReadOnly} />
        </div>
      </div>
    </>
  );

  const renderInquirySettingsFields = () => (
    <>
      <div className="form-group">
        <p className="inquiry-settings-description">
          Set minimum requirements for buyers who wish to send an inquiry about this property.
          <br />
          This helps to filter out irrelevant leads to save you time and money.
        </p>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="requiresAllCash">Require 'All Cash' Payment:</label>
          <input type="checkbox" id="requiresAllCash" name="inquiryRequirements.requiresAllCash" checked={!!formData.inquiryRequirements?.requiresAllCash} onChange={handleChange} disabled={isReadOnly} className="form-checkbox" />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="minExperienceLevel">Minimum Experience Level:</label>
          <select id="minExperienceLevel" name="inquiryRequirements.minExperienceLevel" value={formData.inquiryRequirements?.minExperienceLevel || 'All'} onChange={handleChange} disabled={isReadOnly}>
            {['All', '1+ properties', '3+ properties', '6+ properties', '11+ properties'].map((level) => (
              <option key={level} value={level}>
                {level}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="minPurchaseTimeline">Minimum Purchase Timeline:</label>
          <select id="minPurchaseTimeline" name="inquiryRequirements.minPurchaseTimeline" value={formData.inquiryRequirements?.minPurchaseTimeline || 'All'} onChange={handleChange} disabled={isReadOnly}>
            {['Immediately', 'Within 3 months', 'Within 6 months', 'All'].map((timeline) => (
              <option key={timeline} value={timeline}>
                {timeline}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="minInvolvementLevel">Minimum Involvement Level (1: Any, 10: Must be Hands-On)</label>
          <div className="slider-container">
            <span>1</span>
            <input type="range" id="minInvolvementLevel" name="inquiryRequirements.minInvolvementLevel" min="1" max="10" value={formData.inquiryRequirements?.minInvolvementLevel || 1} onChange={handleChange} disabled={isReadOnly} />
            <span>10</span>
          </div>
          <div className="slider-value">{formData.inquiryRequirements?.minInvolvementLevel || 1}</div>
        </div>
      </div>
    </>
  );

  const renderPhotosFields = () => (
    <>
      <ImageManager
        propertyId={formData.propertyID}
        mainImageURL={formData.mainImageURL}
        propertyImages={formData.propertyImages || []}
        stagedImages={stagedImages || []} // Pass staged images for preview
        onImageUpload={onImageUpload}
        onImageDelete={onImageDelete}
        onDeleteStagedImage={onDeleteStagedImage}
        onSetMainImage={onSetMainImage}
        onImageRotate={onImageRotate}
        onSetMainStagedImage={onSetMainStagedImage}
        showAppNotification={showAppNotification}
        uploadingFiles={uploadingFiles}
        isReadOnly={isReadOnly}
      />
    </>
  );

  // Render based on the active tab for all modes (add, edit, view)
  // isReadOnly prop will disable inputs within the render functions
  switch (activeTab) {
    case 'overview':
      return renderOverviewLocationFields();
    case 'financials':
      return renderFinancialsROIFields();
    case 'expenses':
      return renderRecurringExpensesFields();
    case 'details':
      return renderPropertyDetailsFields();
    case 'inquirySettings':
      return renderInquirySettingsFields();
    case 'photos':
      return renderPhotosFields();
    default:
      return <p>Select a tab</p>; // Should not happen if activeTab is always valid
  }
};

export default PropertyFields;
